// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, WebAuthn (passkey / Face ID) client glue.
//
// The worker uses SimpleWebAuthn server-side and exchanges the standard WebAuthn
// JSON serialization (base64url-encoded challenge / credential ids / response
// buffers). This helper does the spec-compliant conversion both ways and calls
// navigator.credentials, no external dependency, so there's no library/server
// version-mismatch risk and it bundles cleanly.
//
//   passkeyGet(options)   , options = PublicKeyCredentialRequestOptionsJSON from
//                            POST /api/webauthn/auth-options → returns the
//                            AuthenticationResponseJSON to POST to /api/webauthn/auth.
//   passkeyCreate(options), options = PublicKeyCredentialCreationOptionsJSON from
//                            POST /api/webauthn/register-options → returns the
//                            RegistrationResponseJSON to POST to /api/webauthn/register.
//   passkeySupported()    , true when the platform exposes WebAuthn. In a Capacitor
//                            WKWebView this also requires the webcredentials Associated
//                            Domain (entitlement + AASA) for the passkey to resolve.
// ─────────────────────────────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */

// ── base64url ⇄ bytes ─────────────────────────────────────────────────────────
function b64urlToBuf(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface CredDescriptorJSON { id: string; type?: string; transports?: string[] }
interface AuthOptionsJSON {
  challenge: string;
  allowCredentials?: CredDescriptorJSON[];
  [k: string]: any;
}
interface RegOptionsJSON {
  challenge: string;
  user: { id: string; name: string; displayName: string };
  excludeCredentials?: CredDescriptorJSON[];
  [k: string]: any;
}

/** True when WebAuthn is available on this platform. */
export function passkeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).PublicKeyCredential !== 'undefined' &&
    !!(navigator && navigator.credentials && typeof navigator.credentials.get === 'function')
  );
}

/** Authenticate (Face ID / Touch ID). Returns AuthenticationResponseJSON. */
export async function passkeyGet(options: AuthOptionsJSON): Promise<Record<string, any>> {
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...(options as any),
    challenge: b64urlToBuf(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      id: b64urlToBuf(c.id),
      type: 'public-key',
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No credential returned');
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      authenticatorData: bufToB64url(r.authenticatorData),
      signature: bufToB64url(r.signature),
      userHandle: r.userHandle ? bufToB64url(r.userHandle) : undefined,
    },
    authenticatorAttachment: (cred as any).authenticatorAttachment || undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

/** Register a new passkey (Face ID / Touch ID). Returns RegistrationResponseJSON. */
export async function passkeyCreate(options: RegOptionsJSON): Promise<Record<string, any>> {
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...(options as any),
    challenge: b64urlToBuf(options.challenge),
    user: {
      ...(options.user as any),
      id: b64urlToBuf(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      id: b64urlToBuf(c.id),
      type: 'public-key',
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No credential created');
  const r = cred.response as AuthenticatorAttestationResponse;
  const getTransports = (r as any).getTransports;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      attestationObject: bufToB64url(r.attestationObject),
      transports: typeof getTransports === 'function' ? getTransports.call(r) : undefined,
    },
    authenticatorAttachment: (cred as any).authenticatorAttachment || undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

/** True when the user cancelled / dismissed the native passkey sheet (not an error
 *  to surface, just return them to the form). */
export function isPasskeyCancel(err: unknown): boolean {
  const name = (err as any) && (err as any).name;
  return name === 'NotAllowedError' || name === 'AbortError';
}
